/* eslint-disable max-len */
import { ReactElement, ReactNode, useState } from 'react';
import { Form, Input, message, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector, useModal } from 'app/hooks';
import { setUser } from 'app/services/redux/slices';

import {
  SuccessModalBody,
  DeletedAccount,
  ConfirmDelete,
  UpdatePassword,
} from './ModalsBody';

import { getUser, updateUser } from 'app/services/firebase/functions';

import { TYPE_OF_INDUSTRY, User } from 'app/types';

const inputClass =
  'w-full px-3 h-10 bg-white border border-black/10 rounded-lg text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors';
const textAreaClass =
  'w-full px-3 py-2.5 bg-white border border-black/10 rounded-lg text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors resize-y min-h-[120px]';
const labelClass =
  'block text-[11px] font-semibold uppercase tracking-wide text-black/55 mb-2';
const helperErrClass = 'text-[12px] text-red-500 mt-1';

const SectionCard = ({
  title,
  description,
  children,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
}) => (
  <section className="mb-5 rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
    {(title || description) && (
      <header className="px-6 py-4 border-b border-black/[0.08]">
        {title && (
          <h2 className="text-[15px] font-semibold text-black/85 m-0">
            {title}
          </h2>
        )}
        {description && (
          <p className="text-[12px] text-black/55 mt-1 m-0">{description}</p>
        )}
      </header>
    )}
    <div className="p-6">{children}</div>
  </section>
);

const RadioPill = ({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      'flex items-center gap-2.5 w-full text-left px-4 py-3 rounded-xl border transition-all bg-white',
      selected
        ? 'border-black/80 ring-2 ring-black/10'
        : 'border-black/10 hover:border-black/30',
    )}
  >
    <span
      className={classNames(
        'inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors shrink-0',
        selected ? 'border-black/85' : 'border-black/30',
      )}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-black/85" />}
    </span>
    <span className="text-[14px] text-black/85">{children}</span>
  </button>
);

const Checkbox = ({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) => (
  <label className="inline-flex items-center gap-2.5 cursor-pointer select-none text-[14px] text-black/75 py-1">
    <span
      className={classNames(
        'inline-flex items-center justify-center w-5 h-5 rounded border transition-colors shrink-0',
        checked ? 'bg-black/85 border-black/85' : 'bg-white border-black/20',
      )}
    >
      {checked && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 6.5L5 9L9.5 3.5" />
        </svg>
      )}
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="hidden"
    />
    {children}
  </label>
);

const INDUSTRY_OPTIONS: { value: string; label: string }[] = [
  { value: TYPE_OF_INDUSTRY.REAL_ESTATE, label: 'Real Estate' },
  {
    value: TYPE_OF_INDUSTRY.INTERIOR_DESIGN_OR_CONSTRUCTION,
    label: 'Interior Design or Construction',
  },
  {
    value: TYPE_OF_INDUSTRY.A_DIFFERENT_INDUSTRY,
    label: 'A different industry',
  },
  {
    value: TYPE_OF_INDUSTRY.NOT_BUSINESS_RELATED,
    label: 'Not business related',
  },
];

type IFormValues = {
  receiveTipsEmails: boolean;
  receiveNewsEmails: boolean;
  location: string;
  industry: TYPE_OF_INDUSTRY | string;
  username: string;
  differentIndustryName: string;
};

function EditProfile(): ReactElement {
  const savedDataModal = useModal();
  const deletedAccountModal = useModal();
  const confirmDeleteModal = useModal();
  const updatePasswordModal = useModal();

  const { user } = useAppSelector((state) => state.authState);
  const providers: string[] = useAppSelector((state) => state.providers.data);
  const emailProvider = providers.includes('password');
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isDifferentIndustry = (() => {
    const stringifiedIndustry = user?.industry || '';
    const isOneOfIndustryTypes = Object.values(TYPE_OF_INDUSTRY).some(
      (el) => el === stringifiedIndustry,
    );
    return !!user?.industry && !isOneOfIndustryTypes;
  })();

  const [bio, setBio] = useState(user?.bio ?? '');
  const [location, setLocation] = useState(user?.location ?? '');
  const [tipsEmails, setTipsEmails] = useState(
    Boolean(user?.receiveTipsEmails),
  );
  const [newsEmails, setNewsEmails] = useState(
    Boolean(user?.receiveNewsEmails),
  );
  const [industry, setIndustry] = useState<string>(
    isDifferentIndustry
      ? TYPE_OF_INDUSTRY.A_DIFFERENT_INDUSTRY
      : user?.industry || '',
  );
  const [differentIndustryName, setDifferentIndustryName] = useState<string>(
    isDifferentIndustry && user?.industry ? user.industry : '',
  );
  const [bioError, setBioError] = useState(false);
  const [locationError, setLocationError] = useState(false);

  const [updatingState, setUpdatingState] = useState({
    updated: false,
    pending: false,
  });

  const [form] = Form.useForm();

  const onUpdateUserCallback = async () => {
    const updatedUser: User | null = await getUser();
    if (updatedUser) {
      dispatch(setUser(updatedUser));
      if (updatedUser.full_id) {
        message.success(
          intl.formatMessage({
            id: 'editProfile.successUpdatedForm',
            defaultMessage: 'Your changes have been saved.',
          }),
          4,
        );
        setUpdatingState({ pending: false, updated: true });
      }
    }
  };

  const onFinish = async (values: Partial<IFormValues>) => {
    if (!location || !bio) {
      setBioError(!bio);
      setLocationError(!location);
      message.error('Please fill out all required fields.', 4);
      return;
    }

    const allValues = {
      ...values,
      username: values.username || '',
      location,
      differentIndustryName: differentIndustryName || '',
      bio,
      locationInput: location,
      featured: user?.featured_tour_id ?? '',
      featuredTour: user?.featured_tour_id ?? '',
      receiveTipsEmails: tipsEmails,
      receiveNewsEmails: newsEmails,
      industry:
        industry === TYPE_OF_INDUSTRY.A_DIFFERENT_INDUSTRY
          ? differentIndustryName
          : industry,
      UUID: user?.full_id ?? '',
      onUpdateUserCallback,
    };

    try {
      setUpdatingState({ updated: false, pending: true });
      await updateUser(allValues);
    } catch {
      setUpdatingState({ updated: false, pending: false });
      message.error('Update failed. Please try again.', 4);
    }
  };

  return (
    <div className="flex-1 bg-white relative overflow-y-auto py-8 px-8 max-md:py-6 max-md:px-5 max-md:pb-10">
      <div className="mb-7">
        <h1 className="text-[26px] font-bold tracking-tight m-0 text-[#151A30]">
          <FormattedMessage id="editProfile.header" defaultMessage="Profile" />
        </h1>
        <p className="text-black/55 mt-1 text-[13px] m-0">
          Manage your profile, account, and notification preferences.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        initialValues={{
          username: user?.name || '',
          email: user?.email || '',
        }}
      >
        <SectionCard>
          <div className="grid grid-cols-2 gap-5 max-md:grid-cols-1">
            <div>
              <label className={labelClass}>Username</label>
              <Form.Item name="username" className="!mb-0">
                <Input className={inputClass} placeholder="Your name" />
              </Form.Item>
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  if (e.target.value) setLocationError(false);
                }}
                onBlur={() => setLocationError(!location)}
                placeholder="City, State"
                className={inputClass}
              />
              {locationError && (
                <p className={helperErrClass}>Please enter your location.</p>
              )}
            </div>
          </div>

          <div className="mt-5">
            <label className={labelClass}>About</label>
            <textarea
              value={bio}
              onChange={(e) => {
                setBio(e.target.value);
                if (e.target.value) setBioError(false);
              }}
              onBlur={() => setBioError(!bio)}
              placeholder="A short description of what you do"
              className={textAreaClass}
            />
            {bioError && (
              <p className={helperErrClass}>Please add a short bio.</p>
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Industry"
          description="Helps us tailor the dashboard to your work."
        >
          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
            {INDUSTRY_OPTIONS.map((opt) => (
              <RadioPill
                key={opt.value}
                selected={industry === opt.value}
                onClick={() => {
                  setIndustry(opt.value);
                  // Clear sub-input when leaving "different industry"
                  if (opt.value !== TYPE_OF_INDUSTRY.A_DIFFERENT_INDUSTRY) {
                    setDifferentIndustryName('');
                  }
                }}
              >
                {opt.label}
              </RadioPill>
            ))}
          </div>
          {industry === TYPE_OF_INDUSTRY.A_DIFFERENT_INDUSTRY && (
            <div className="mt-4">
              <label className={labelClass}>What industry?</label>
              <input
                type="text"
                value={differentIndustryName}
                onChange={(e) => setDifferentIndustryName(e.target.value)}
                placeholder="Tell us about your industry"
                className={inputClass}
              />
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Email Preferences"
          description="Choose which emails you'd like to receive."
        >
          <div className="flex flex-col gap-1">
            <Checkbox checked={tipsEmails} onChange={setTipsEmails}>
              I&rsquo;d like tips and tricks for getting more out of The 3D App.
            </Checkbox>
            <Checkbox checked={newsEmails} onChange={setNewsEmails}>
              I&rsquo;d like the latest news and product updates.
            </Checkbox>
          </div>
        </SectionCard>

        <SectionCard
          title="Account"
          description="Security and account-level actions."
        >
          <div className="flex flex-col gap-2">
            {emailProvider && (
              <button
                type="button"
                onClick={updatePasswordModal.open}
                className="btn-chip self-start"
              >
                Update password
              </button>
            )}
            <button
              type="button"
              onClick={confirmDeleteModal.open}
              className="inline-flex items-center gap-1.5 self-start px-3 h-9 rounded-full border border-red-500/30 bg-transparent text-[12px] font-medium text-red-600 cursor-pointer hover:bg-red-500/[0.06] hover:border-red-500/60 transition-colors"
            >
              Delete account
            </button>
          </div>
        </SectionCard>

        <div className="flex items-center justify-end gap-2 pb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-chip"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updatingState.pending}
            className="btn-primary"
          >
            {updatingState.pending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Form>

      <Modal
        className="glass-modal"
        footer={null}
        centered
        visible={savedDataModal.isOpened}
        onCancel={savedDataModal.close}
        maskStyle={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <SuccessModalBody data={savedDataModal} />
      </Modal>

      <Modal
        className="glass-modal"
        footer={null}
        centered
        visible={confirmDeleteModal.isOpened}
        onCancel={confirmDeleteModal.close}
        maskStyle={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <ConfirmDelete data={confirmDeleteModal} />
      </Modal>

      <Modal
        className="glass-modal"
        footer={null}
        centered
        visible={deletedAccountModal.isOpened}
        onCancel={deletedAccountModal.close}
        maskStyle={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <DeletedAccount data={deletedAccountModal} />
      </Modal>

      <Modal
        className="glass-modal"
        footer={null}
        centered
        visible={updatePasswordModal.isOpened}
        onCancel={updatePasswordModal.close}
        maskStyle={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <UpdatePassword data={updatePasswordModal} />
      </Modal>
    </div>
  );
}

export default EditProfile;
